import { devConfig } from 'config/config'; // Importing devConfig from configuration

// MIME types by extension, used to build the data URI from the base64 File
const documentMimeTypes = {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
};

/**
 * File name shown/downloaded for a document: its Name plus the extension taken from Route.
 * @param {string} name - Document name.
 * @param {string} extension - File extension without dot.
 * @returns {string} The file name.
 */
export function buildDocumentFileName(name, extension) {
    return (name || 'documento') + (extension ? '.' + extension : '');
}

/**
 * Triggers the browser download of a data URI through a temporary link.
 * @param {string} url - Data URI of the file.
 * @param {string} fileName - Name for the downloaded file.
 * @returns {boolean} True if the download was triggered without errors.
 */
export function downloadDataUri(url, fileName) {
    try {
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return true;
    } catch (e) {
        return false;
    }
}

/**
 * Audit record of a document file download (Report/informationDownloadedByUser), silent on success and error.
 * @param {Object} item - Document data (Id, Version, Name).
 * @param {string} extension - File extension without dot.
 */
export function registerDocumentDownload(item, extension) {
    webix
        .ajax()
        .headers({
            'Content-type': 'application/json',
        })
        .post(devConfig.geturlApi() + 'Report/informationDownloadedByUser', {
            NameReport: buildDocumentFileName(item.Name, extension),
            TypeReport: (extension || '').toUpperCase(),
            Filters: webix.storage.local.get('urlAjaxGlobal'), // Last request: GetDocumentFile with Id and Version
            IdDocument: item.Id,
            File: 'S', // Always 'S' for file downloads
            Version: String(item.Version || ''), // String to keep versions like 2.0
        });
}

/**
 * Converts a base64 data URI to a Blob URL; the PDF viewer honors #toolbar=0 more reliably on Blob URLs.
 * @param {string} url - Data URI of the file.
 * @returns {string} The Blob URL.
 */
export function dataUriToBlobUrl(url) {
    const commaIndex = url.indexOf(',');
    const mimeType = url.substring(5, url.indexOf(';'));
    const binary = atob(url.substring(commaIndex + 1));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return URL.createObjectURL(new Blob([bytes], { type: mimeType }));
}

/**
 * Next version adding one tenth to the current one (1.0 -> 1.1, 1.9 -> 2.0), used by every AddVersion.
 * @param {string} version - Current version.
 * @returns {string} The next version with one decimal, or '' if it is not a number.
 */
export function getNextVersion(version) {
    const current = parseFloat(version);
    if (isNaN(current)) {
        return '';
    }
    // Works in tenths to avoid floating point errors (1.1 + 0.1 = 1.2000000000000002)
    return ((Math.round(current * 10) + 1) / 10).toFixed(1);
}

/**
 * Returns the error text for an invalid document file (extension or size > 5.000 KB), or '' if valid.
 * @param {Object} uploadedFile - File object from the Webix uploader.
 * @param {Function} _ - Localization function.
 * @returns {string} The error text.
 */
export function getDocumentFileError(uploadedFile, _) {
    const allowedExtensions = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'];
    const maxFileSize = 5000 * 1024; // Max size in bytes (5.000 KB)
    const extension = uploadedFile.name.split('.').pop().toLowerCase();
    if (allowedExtensions.indexOf(extension) === -1) {
        return _('El formato del archivo no es válido');
    }
    if (uploadedFile.size > maxFileSize) {
        return _('El tamaño máximo permitido es de 5MB');
    }
    return '';
}

/**
 * Retrieves the stored file of a document (RegisteredDocument/GetDocumentFile) as a data URI.
 * @param {Object} item - Document data (Id, Version).
 * @param {Function} onSuccess - Receives (dataUri, extension).
 * @param {Function} onError - Called on request error, backend error or empty file.
 */
export function getDocumentFile(item, onSuccess, onError) {
    webix
        .ajax()
        .get(
            devConfig.geturlApi() +
                'RegisteredDocument/GetDocumentFile?Id=' +
                encodeURIComponent(item.Id) +
                '&Version=' +
                encodeURIComponent(item.Version || ''),
            {
                error: onError,
                success: function (text) {
                    let json;
                    try {
                        json = JSON.parse(text);
                    } catch (e) {
                        onError();
                        return;
                    }
                    if (json.HasError || !json.data || !json.data.File) {
                        onError();
                        return;
                    }
                    const extensionMatch = String(json.data.Route || '').match(/\.([a-z0-9]+)$/i); // File extension from Route
                    const extension = extensionMatch ? extensionMatch[1].toLowerCase() : '';
                    const file = json.data.File;
                    // File comes as plain base64; keep it as is if it is already a data URI
                    const url =
                        file.indexOf('data:') === 0
                            ? file
                            : 'data:' + (documentMimeTypes[extension] || 'application/octet-stream') + ';base64,' + file;
                    onSuccess(url, extension);
                },
            },
        );
}

/**
 * Opens the "Visualizar archivo" window and shows the stored file of a document, same flow as GetPaymentDoc in depositRequests.
 * @param {Object} item - Document data (Id, Version, Name).
 * @param {Function} _ - Localization function.
 * @param {boolean} [canDownload] - Download permission of the calling module; defaults to registeredDocumentDownload.
 * @param {boolean} [audit] - Registers the download audit (informationDownloadedByUser); only Documentos registrados uses it.
 */
export function showDocumentFile(item, _, canDownload, audit) {
    if (canDownload === undefined) {
        canDownload = devConfig.getPermAction('registeredDocumentDownload', 'Add'); // Same permission as the download icon
    }
    // Creating the file window if it does not exist
    if (!$$('documentFileWindow')) {
        webix.ui({
            view: 'window',
            id: 'documentFileWindow',
            width: 700,
            height: 500,
            position: 'center',
            modal: true,
            head: {
                cols: [
                    { template: _('Visualizar archivo'), type: 'header', borderless: true }, // Window title
                    {
                        view: 'icon',
                        id: 'documentFileDownloadBtn',
                        icon: 'wxi-download',
                        tooltip: _('Descargar'),
                        hidden: true, // Hidden until the file is loaded, and only shown with download permission
                        css: 'prm-registeredDocumentDownload',
                        click: function () {
                            // Download button click handler
                            const win = $$('documentFileWindow'); // Getting window instance
                            if (!win.downloadUrl || !win.canDownload) return; // Exit if no URL or permission of the calling module
                            if (downloadDataUri(win.downloadUrl, win.downloadName) && win.audit) {
                                registerDocumentDownload(win.downloadItem, win.downloadExtension); // Audit only when the calling module requires it
                            }
                        },
                    },
                    {
                        view: 'icon',
                        icon: 'wxi-close',
                        click: function () {
                            // Close button handler
                            $$('documentFileWindow').hide();
                        },
                    },
                ],
            },
            body: {
                rows: [
                    {
                        view: 'template',
                        id: 'documentFileContent',
                        template: '',
                        borderless: true,
                    },
                ],
            },
            on: {
                onHide: function () {
                    // Cleanup when window is closed
                    $$('documentFileContent').setHTML(''); // Clear content
                    this.downloadUrl = null; // Reset stored URL
                    this.downloadItem = null;
                    if (this.blobUrl) {
                        URL.revokeObjectURL(this.blobUrl); // Release the PDF Blob URL
                        this.blobUrl = null;
                    }
                    $$('documentFileDownloadBtn').hide(); // Hide download button
                },
            },
        });
    }

    $$('documentFileContent').setHTML(_('Cargando...')); // Loading state
    $$('documentFileWindow').show(); // Displaying the window

    // Request to retrieve the document file (Id and Version of the document)
    getDocumentFile(
        item,
        function (url, extension) {
            const mimeType = url.substring(5, url.indexOf(';')); // e.g. image/png, application/pdf
            let html = ''; // Variable to hold rendered HTML

            const win = $$('documentFileWindow');
            win.canDownload = canDownload; // Checked again by the download button
            win.audit = !!audit; // Set on every open, the window is shared between modules
            win.downloadUrl = url; // Storing URL for download usage
            win.downloadName = buildDocumentFileName(item.Name, extension);
            win.downloadItem = item; // Id, Version and Name for the download audit
            win.downloadExtension = extension;

            if (mimeType.indexOf('image/') === 0) {
                // Images (JPG, JPEG, PNG) are shown inline
                html = "<img src='" + url + "' style='max-width:100%;max-height:100%;display:block;margin:auto;' />";
            } else if (mimeType === 'application/pdf') {
                // PDF is shown in an iframe without the viewer toolbar; download is only offered by the window button
                win.blobUrl = dataUriToBlobUrl(url);
                html =
                    "<iframe src='" +
                    win.blobUrl +
                    "#toolbar=0&navpanes=0' style='width:100%;height:100%;border:0'></iframe>";
            } else {
                // DOC/DOCX can't be previewed in the browser, only downloaded (if permitted)
                html =
                    "<div style='margin:40px;text-align:center;font-size:16px;'>" +
                    (canDownload
                        ? _('Vista previa no disponible para Doc y Docx, descarga el archivo')
                        : _('Vista previa no disponible para Doc y Docx')) +
                    '</div>';
            }

            if (canDownload) {
                $$('documentFileDownloadBtn').show(); // Download available for every type
            }
            $$('documentFileContent').setHTML(html); // Rendering content in template
        },
        function () {
            // Request error, backend error or empty file
            webix.message(_('No se pudo obtener el archivo'));
            $$('documentFileWindow').hide();
        },
    );
}
